<template>
  <v-text-field ref="field"
                v-bind="$attrs"
                v-on="listeners"
                :value="text"
                inputmode="decimal"
                @input="onInput"
  ></v-text-field>
</template>

<script>
// v-text-field that shows numbers with thousand separators ("1 000 000")
// while keeping a plain Number in v-model.
const groupThousands = (intPart) => intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')

export default {
  name: 'money-field',

  inheritAttrs: false,

  props: {
    value: [Number, String],
    precision: {type: Number, default: 2},
  },

  data() {
    return {
      text: this.format(this.value),
    }
  },

  computed: {
    listeners() {
      // eslint-disable-next-line no-unused-vars
      const {input, ...rest} = this.$listeners
      return rest
    },
  },

  watch: {
    value(value) {
      if (this.parse(this.text) !== this.toNumber(value)) {
        this.text = this.format(value)
      }
    },

    precision() {
      this.onInput(this.text)
    },
  },

  methods: {
    toNumber(value) {
      if (value === null || value === undefined || value === '') return null
      const number = parseFloat(value)
      return isNaN(number) ? null : number
    },

    format(value) {
      const number = this.toNumber(value)
      if (number === null) return ''

      let [intPart, decPart] = String(value).replace(/\s/g, '').split('.')
      if ((decPart || '').length > this.precision) {
        [intPart, decPart] = number.toFixed(this.precision).split('.')
      }

      return groupThousands(intPart) + (decPart ? '.' + decPart : '')
    },

    // Keeps digits and a single decimal separator (comma is accepted as dot)
    clean(raw) {
      let [intPart, ...rest] = String(raw || '').replace(/,/g, '.').replace(/[^\d.]/g, '').split('.')
      intPart = intPart.replace(/^0+(?=\d)/, '')

      if (!this.precision || !rest.length) return intPart
      return (intPart || '0') + '.' + rest.join('').slice(0, this.precision)
    },

    parse(text) {
      return this.toNumber(this.clean(text))
    },

    onInput(raw) {
      const input = this.$refs.field?.$refs.input
      const caret = input && document.activeElement === input ? input.selectionStart : null
      // Characters (digits and dot) before the caret survive reformatting
      const significantBeforeCaret = caret === null
        ? null
        : String(raw).slice(0, caret).replace(/,/g, '.').replace(/[^\d.]/g, '').length

      const cleaned = this.clean(raw)
      const [intPart, decPart] = cleaned.split('.')
      this.text = cleaned === '' ? '' : groupThousands(intPart) + (decPart !== undefined ? '.' + decPart : '')

      // Force the DOM to match even when this.text did not change (e.g. a rejected letter)
      if (input) input.value = this.text

      this.$emit('input', this.toNumber(cleaned))

      if (significantBeforeCaret !== null) {
        this.$nextTick(() => this.restoreCaret(input, significantBeforeCaret))
      }
    },

    restoreCaret(input, significantCount) {
      let position = 0
      let seen = 0
      while (position < this.text.length && seen < significantCount) {
        if (this.text[position] !== ' ') seen++
        position++
      }
      input.setSelectionRange(position, position)
    },
  },
}
</script>
